import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  TableContainer,
  Button,
  Snackbar,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import axios from "axios";
import ElectionDataTable from "../components/ElectionTable";
import DataTable from "../components/TableComponent";
import FileSaver from "file-saver";

const Profile = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [supportStatus, setSupportStatus] = useState(null);
  const tableRef = useRef(null);
  const leftColumnRef = useRef(null);
  const [leftColumnHeight, setLeftColumnHeight] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [tag, setTag] = useState("");
  const [isEditingTag, setIsEditingTag] = useState(false);
  const [additionalEmails, setAdditionalEmails] = useState([]);
  const [additionalPhones, setAdditionalPhones] = useState([]);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [contactSnackbar, setContactSnackbar] = useState({ open: false, message: "" });

  const handleSupportStatusReset = () => {
    setSelectedOption(null);
    setTag("");
    updateStatus("", "");
  };

  const handleClick = (event, option) => {
    switch (event.detail) {
      case 1: {
        break;
      }
      case 2: {
        console.log("double click");
        if (selectedOption === option) {
          setSelectedOption(null);
        } else {
          setSelectedOption(option);
          updateStatus(option, tag);
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  const fetchData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/get_user_data/${id}`
      );
      console.log("data", res.data);

      if (res.data) {
        setData(res.data);
        setAdditionalEmails(res.data.additional_emails || []);
        setAdditionalPhones(res.data.additional_phones || []);
        loadCount();
      } else {
        console.log("Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateStatus = async (status, tagValue = tag) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/update_status`,
        {
          voter_ID: id,
          status: status,
          tag: tagValue,
        }
      );

      const newindex = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/newindex_status`,
        {
          voter_ID: id,
          status: status,
          tag: tagValue,
        }
      );
      console.log(newindex.data);
      console.log(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getSupportStatus = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/get_status/${id}`
      );
      setSelectedOption(res.data.Status);
      setTag(res.data.Tag || "");
    } catch (error) {
      console.log(error);
    }
  };

  const handleAddEmail = async () => {
    if (!newEmail.trim()) return;
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setContactSnackbar({ open: true, message: "Invalid email format" });
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/add_additional_contact`,
        {
          voter_ID: id,
          contact_type: "email",
          contact_value: newEmail.trim()
        }
      );
      
      const newEmailObj = { email: newEmail.trim(), added_date: new Date().toISOString() };
      setAdditionalEmails([...additionalEmails, newEmailObj]);
      setNewEmail("");
      setIsEditingEmail(false);
      setContactSnackbar({ open: true, message: "Email added successfully" });
    } catch (error) {
      console.log(error);
      setContactSnackbar({ open: true, message: error.response?.data?.error || "Failed to add email" });
    }
  };

  const handleAddPhone = async () => {
    if (!newPhone.trim()) return;
    
    const phoneDigits = newPhone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setContactSnackbar({ open: true, message: "Invalid phone number" });
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/add_additional_contact`,
        {
          voter_ID: id,
          contact_type: "phone",
          contact_value: phoneDigits
        }
      );
      
      const newPhoneObj = { phone: phoneDigits, added_date: new Date().toISOString() };
      setAdditionalPhones([...additionalPhones, newPhoneObj]);
      setNewPhone("");
      setIsEditingPhone(false);
      setContactSnackbar({ open: true, message: "Phone added successfully" });
    } catch (error) {
      console.log(error);
      setContactSnackbar({ open: true, message: error.response?.data?.error || "Failed to add phone" });
    }
  };

  const handleDeleteEmail = async (emailValue) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/delete_additional_contact`,
        {
          data: {
            voter_ID: id,
            contact_type: "email",
            contact_value: emailValue
          }
        }
      );
      
      setAdditionalEmails(additionalEmails.filter(e => e.email !== emailValue));
      setContactSnackbar({ open: true, message: "Email deleted successfully" });
    } catch (error) {
      console.log(error);
      setContactSnackbar({ open: true, message: "Failed to delete email" });
    }
  };

  const handleDeletePhone = async (phoneValue) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/delete_additional_contact`,
        {
          data: {
            voter_ID: id,
            contact_type: "phone",
            contact_value: phoneValue
          }
        }
      );
      
      setAdditionalPhones(additionalPhones.filter(p => p.phone !== phoneValue));
      setContactSnackbar({ open: true, message: "Phone deleted successfully" });
    } catch (error) {
      console.log(error);
      setContactSnackbar({ open: true, message: "Failed to delete phone" });
    }
  };

  const handleDeleteExistingPhone = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/delete_existing_contact`,
        {
          data: {
            voter_ID: id,
            contact_type: "phone"
          }
        }
      );
      
      // Update the data state to remove the phone
      setData({...data, Home_Phone: null});
      setContactSnackbar({ open: true, message: "Phone deleted successfully" });
    } catch (error) {
      console.log(error);
      setContactSnackbar({ open: true, message: "Failed to delete phone" });
    }
  };

  const handleDeleteExistingEmail = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/delete_existing_contact`,
        {
          data: {
            voter_ID: id,
            contact_type: "email"
          }
        }
      );
      
      // Update the data state to remove the email
      setData({...data, Email: null, Mail_Country: null});
      setContactSnackbar({ open: true, message: "Email deleted successfully" });
    } catch (error) {
      console.log(error);
      setContactSnackbar({ open: true, message: "Failed to delete email" });
    }
  };

  const [democratVoters, setDemocratVoters] = useState([]);
  const [showDemocratDataTable, setShowDemocratDataTable] = useState(false);
  const [DemocratCount, setDemocratCount] = useState(0);
  const [RepublicanCount, setRepublicanCount] = useState(0);
  const [TotalCount, setTotalCount] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [loadingTableData, setLoadingTableData] = useState(false);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleCopyToClipboardVCF = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/download_vcard/${data.ID_Number}`,
        { responseType: "blob" }
      );

      // Create Blob from response data
      const blob = new Blob([response.data], { type: "text/vcard" });

      // Save Blob to a file using FileSaver.js
      FileSaver.saveAs(blob, `${data.ID_Number}_vcard.vcf`);

      // Optional: Copy to clipboard using navigator.clipboard.write
      const text = await response.data.text();
      await navigator.clipboard.writeText(text);

      console.log("vCard downloaded and copied to clipboard successfully");
    } catch (error) {
      console.error("Error during vCard download:", error);
    }
  };

  const handleDownload = (type) => {
    if (type === "CSV") {
      handleCopyToClipboard();
    } else {
      console.log(`Download ${type}`);
    }
  };
  const toTitleCase = (str) => {
    return str.replace(/\w\S*/g, function (txt) {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };
  const handleCopyToClipboard = () => {
    const commaDelimitedFields = [
      "ID_Number",
      "County",
      "First_Name",
      "Last_Name",
      "Middle_Name",
      "Suffix",
      "Gender",
      "Party",
      "House_Number",
      "Street_Name",
      "Apartment_Number",
      "Address_Line_2",
      "City",
      "State",
      "Zip",
      "Home_Phone",
      "Email",
    ];

    const headerRow = commaDelimitedFields.join(",") + "\n";

    const dataRow =
      commaDelimitedFields.map((field) => `"${data[field] || ""}"`).join(",") +
      "\n";
    // const dataRow =
    //   commaDelimitedFields
    //     .map((field) => {
    //       let value = data[field];
    //       if (value === null || value === "") {
    //         return "";
    //       }
    //       if (!isNaN(value) && Number.isInteger(Number(value))) {
    //         value = Number(value).toString();
    //       }
    //       return `"${value}"`;
    //     })
    //     .join(",") + "\n";

    const csvData = dataRow;

    navigator.clipboard.writeText(csvData);

    setSnackbarOpen(true);
  };

  const handleDemocratVoterLink = async () => {
    try {
      setLoadingTableData(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/search/democrat`,
        {
          filters: {
            Street_Name: data?.Street_Name,
            House_Number: data?.House_Number,
            Precinct_Name: data?.Precinct_Name,
          },
        }
      );

      if (res.data && res.data.data) {
        setDemocratVoters(res.data.data);
        setShowDemocratDataTable(true);
      } else {
        console.log("Democrat Voter Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTableData(false);
    }
  };
  const handleRepublicianVoterLink = async () => {
    try {
      setLoadingTableData(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/search/republician`,
        {
          filters: {
            Street_Name: data?.Street_Name,
            House_Number: data?.House_Number,
            Precinct_Name: data?.Precinct_Name,
          },
        }
      );

      if (res.data && res.data.data) {
        setDemocratVoters(res.data.data);
        setShowDemocratDataTable(true);
      } else {
        console.log("Democrat Voter Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTableData(false);
    }
  };
  const handleAllVoterLink = async () => {
    try {
      setLoadingTableData(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/search/allvoters`,
        {
          filters: {
            Street_Name: data?.Street_Name,
            House_Number: data?.House_Number,
            Precinct_Name: data?.Precinct_Name,
          },
        }
      );

      if (res.data && res.data.data) {
        setDemocratVoters(res.data.data);
        setTotalCount(res.data.data.length);
        setShowDemocratDataTable(true);
      } else {
        console.log("Democrat Voter Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTableData(false);
    }
  };

  const handleStreetLink = async () => {
    try {
      setLoadingTableData(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/search/street`,
        {
          filters: {
            Street_Name: data?.Street_Name,
            Precinct_Name: data?.Precinct_Name,
          },
        }
      );

      if (res.data && res.data.data) {
        setDemocratVoters(res.data.data);
        setShowDemocratDataTable(true);
        tableRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        console.log("Democrat Voter Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTableData(false);
    }
  };
  const handleCongressLink = async () => {
    try {
      setLoadingTableData(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/search/congress`,
        {
          filters: {
            County: data?.County,
            "U.S._Congress": data?.["U.S._Congress"],
          },
        }
      );

      if (res.data && res.data.data) {
        setDemocratVoters(res.data.data);
        setShowDemocratDataTable(true);
        tableRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        console.log("Democrat Voter Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTableData(false);
    }
  };

  const handleStateHouseLink = async () => {
    try {
      setLoadingTableData(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/search/statehouse`,
        {
          filters: {
            County: data?.County,
            State_House: data?.State_House,
          },
        }
      );

      if (res.data && res.data.data) {
        setDemocratVoters(res.data.data);
        setShowDemocratDataTable(true);
        tableRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        console.log("Democrat Voter Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTableData(false);
    }
  };
  const handleStateDistrictLink = async () => {
    try {
      setLoadingTableData(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/search/statedistrict`,
        {
          filters: {
            School_District: data?.School_District,
            County: data?.County,
          },
        }
      );

      if (res.data && res.data.data) {
        setDemocratVoters(res.data.data);
        setShowDemocratDataTable(true);
        tableRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        console.log("Democrat Voter Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTableData(false);
    }
  };

  const handlePrecinctNameLink = async () => {
    try {
      setLoadingTableData(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/search/precinctname`,
        {
          filters: {
            Precinct_Name: data?.Precinct_Name,
            County: data?.County,
          },
        }
      );

      if (res.data && res.data.data) {
        setDemocratVoters(res.data.data);
        setShowDemocratDataTable(true);
        tableRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        console.log("Democrat Voter Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTableData(false);
    }
  };

  const handleStateSenateLink = async () => {
    try {
      setLoadingTableData(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/search/statesenate`,
        {
          filters: {
            County: data?.County,
            State_Senate: data?.State_Senate,
          },
        }
      );

      if (res.data && res.data.data) {
        setDemocratVoters(res.data.data);
        setShowDemocratDataTable(true);
        tableRef.current.scrollIntoView({ behavior: "smooth" });
      } else {
        console.log("Democrat Voter Data is empty or undefined.");
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingTableData(false);
    }
  };

  const loadCount = async () => {
    if (data) {
      console.log("Data is", data);
      try {
        const [democratRes, republicanRes, allVotersRes] = await Promise.all([
          axios.post(`${process.env.REACT_APP_BACKEND_URL}/search/democrat`, {
            filters: {
              Street_Name: data?.Street_Name,
              House_Number: data?.House_Number,
              Precinct_Name: data?.Precinct_Name,
            },
          }),
          axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/search/republician`,
            {
              filters: {
                Street_Name: data?.Street_Name,
                House_Number: data?.House_Number,
                Precinct_Name: data?.Precinct_Name,
              },
            }
          ),
          axios.post(`${process.env.REACT_APP_BACKEND_URL}/search/allvoters`, {
            filters: {
              Street_Name: data?.Street_Name,
              House_Number: data?.House_Number,
              Precinct_Name: data?.Precinct_Name,
            },
          }),
        ]);

        if (democratRes.data && democratRes.data.data) {
          setDemocratCount(democratRes.data.data.length);
        } else {
          console.log("Democrat Voter Data is empty or undefined.");
        }

        if (republicanRes.data && republicanRes.data.data) {
          setRepublicanCount(republicanRes.data.data.length);
        } else {
          console.log("Republican Voter Data is empty or undefined.");
        }

        if (allVotersRes.data && allVotersRes.data.data) {
          setTotalCount(allVotersRes.data.data.length);
        } else {
          console.log("All Voters Data is empty or undefined.");
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    fetchData();
    getSupportStatus();
  }, [id]);

  useEffect(() => {
    const el = leftColumnRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      // Use offsetHeight so we get the true rendered height (content + padding + border)
      setLeftColumnHeight(el.offsetHeight);
    });
    observer.observe(el);
    // Set initial height
    setLeftColumnHeight(el.offsetHeight);
    return () => observer.disconnect();
  }, [data]);
  const tableContainerRef = useRef(null);
  useEffect(() => {
    if (loadingTableData && tableContainerRef.current) {
      tableContainerRef.current.scrollTop =
        tableContainerRef.current.scrollHeight;
    }
    if (data) {
      loadCount();
    }
  }, [data, loadingTableData]);
  const navigate = useNavigate();

  return (
    <div>
      {data ? (
        <div className="p-1 sm:p-4 bg-gray-100">
          <div className="py-2 sm:py-4 px-1 sm:px-8 flex flex-row gap-2 justify-between">
            <Button
              variant="outlined"
              color="primary"
              onClick={() => navigate("/")}
              sx={{ 
                flex: { xs: '0 0 auto', sm: '0 0 auto' },
                width: { xs: '100px', sm: 'auto' },
                padding: { xs: '10px 16px', sm: '6px 16px' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' }
              }}
            >
              Back
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleSupportStatusReset}
              sx={{ 
                flex: { xs: '1 1 auto', sm: '0 0 auto' },
                fontSize: { xs: '0.875rem', sm: '0.875rem' },
                padding: { xs: '10px 16px', sm: '6px 16px' },
                whiteSpace: 'nowrap',
                textAlign: 'center'
              }}
            >
              Reset Support Status
            </Button>
          </div>
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-2 sm:mb-4 px-1 sm:px-8 gap-2 sm:gap-4">
            <div className="flex items-center w-full lg:w-auto">
              <div className="ml-0 sm:ml-3">
                <Typography variant="h5" className="sm:text-4xl">
                  {toTitleCase(data.First_Name)} {toTitleCase(data.Last_Name)}
                </Typography>
                <Typography variant="subtitle2" className="sm:text-base text-gray-600">
                  Age: {Math.floor(data.Age)} Gender: {data.Gender}
                </Typography>
                <Typography variant="subtitle2" className="sm:text-base text-gray-600">
                  Party: {data.Party_Code}
                </Typography>
              </div>
            </div>
           
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2 sm:gap-4 py-2 w-full lg:w-auto">
              {/* Desktop: Tag and Petition first, Mobile: Action buttons first */}
              <div className="flex flex-col gap-2 w-full lg:w-auto order-2 lg:order-1">
                {!isEditingTag ? (
                  <Box
                    onClick={() => setIsEditingTag(true)}
                    sx={{
                      position: 'relative',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      padding: '8.5px 14px',
                      height: '40px',
                      cursor: 'pointer',
                      width: '100%',
                      '@media (min-width: 1024px)': {
                        width: '200px',
                      },
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'border-color 0.2s',
                      '&:hover': {
                        borderColor: 'rgba(0, 0, 0, 0.87)',
                      },
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        position: 'absolute',
                        top: '-9px',
                        left: '10px',
                        backgroundColor: '#f3f4f6',
                        padding: '0 5px',
                        fontSize: '0.75rem',
                        color: 'rgba(0, 0, 0, 0.6)',
                        lineHeight: 1,
                      }}
                    >
                      Tag
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        fontSize: '1rem',
                        color: tag ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        width: '100%',
                      }}
                    >
                      {tag || 'Click to edit'}
                    </Typography>
                  </Box>
                ) : (
                  <div className="flex gap-2 items-center">
                    <TextField
                      label="Tag"
                      variant="outlined"
                      size="small"
                      value={tag}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 20) {
                          setTag(value);
                        }
                      }}
                      inputProps={{ maxLength: 20 }}
                      sx={{ 
                        flex: 1,
                        '@media (min-width: 1024px)': { 
                          width: '200px',
                          flex: 'none'
                        } 
                      }}
                      autoFocus
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => {
                        updateStatus(selectedOption, tag);
                        setIsEditingTag(false);
                      }}
                      sx={{ flexShrink: 0 }}
                    >
                      Save
                    </Button>
                  </div>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  style={{ whiteSpace: "nowrap" }}
                  onClick={() => window.open(`/petition/petition-management?id=${id}`, '_blank')}
                  sx={{ width: '100%', '@media (min-width: 1024px)': { width: 'auto' } }}
                >
                  Add to Petition
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-1 sm:gap-2 w-full lg:w-auto order-1 lg:order-2 mb-2 sm:mb-4 lg:mb-0">
                <Button
                  variant="contained"
                  color="primary"
                  className="w-full"
                  onClick={(event) => handleClick(event, "Support")}
                  disabled={selectedOption === "Support"}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '6px 16px' } }}
                >
                  Support
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="w-full"
                  onClick={(event) => handleClick(event, "Unsure")}
                  disabled={selectedOption === "Unsure"}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '6px 16px' } }}
                >
                  Unsure
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="w-full"
                  onClick={handleCopyToClipboardVCF}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '6px 16px' } }}
                >
                  VCF
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="w-full"
                  onClick={(event) => handleClick(event, "No Support")}
                  disabled={selectedOption === "No Support"}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '6px 16px' } }}
                >
                  No Support
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="w-full"
                  onClick={(event) => handleClick(event, "No Response")}
                  disabled={selectedOption === "No Response"}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '6px 16px' } }}
                >
                  No Response
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  className="w-full"
                  onClick={() => handleDownload("CSV")}
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, padding: { xs: '8px 4px', sm: '6px 16px' } }}
                >
                  CSV
                </Button>
              </div>
            </div>
          </div>
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={2000}
            onClose={handleSnackbarClose}
            message="CSV data copied to clipboard"
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          />
          <Snackbar
            open={contactSnackbar.open}
            autoHideDuration={3000}
            onClose={() => setContactSnackbar({ open: false, message: "" })}
            message={contactSnackbar.message}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          />
          <div className="flex flex-col lg:flex-row gap-4 sm:gap-5 px-1 py-2 sm:px-8">
            <div ref={leftColumnRef} className="flex-col gap-4 w-full lg:w-auto" style={{ alignSelf: 'flex-start' }}>
              <Card elevation={3} className="mb-4 pr-0 sm:pr-5">
                <CardContent className="px-2 py-3 sm:px-4 sm:py-4">

                  <Typography variant="h6" className="pb-2 text-blue-500 text-base sm:text-xl">
                    Household Voters
                  </Typography>
                  <Divider className="my-2" />

                  <Typography variant="subtitle1" className="pt-2 text-sm sm:text-base">
                    Total Voters in Household:
                    <span
                      className="text-blue-500 underline cursor-pointer px-2"
                      onClick={handleAllVoterLink}
                    >
                      {TotalCount}
                    </span>
                  </Typography>
                  <Typography variant="subtitle1" className="text-sm sm:text-base">
                    Total Democratic Voters:
                    <span
                      className="text-blue-500 underline cursor-pointer px-2"
                      onClick={handleDemocratVoterLink}
                    >
                      {DemocratCount}
                    </span>
                  </Typography>
                  <Typography variant="subtitle1" className="text-sm sm:text-base">
                    Total Republican Voters:
                    <span
                      className="text-blue-500 underline cursor-pointer px-2"
                      onClick={handleRepublicianVoterLink}
                    >
                      {RepublicanCount}
                    </span>
                  </Typography>
                </CardContent>
              </Card>
              <Card elevation={3}>
                <CardContent>
                  <Grid container spacing={1}></Grid>
                  <Typography variant="h6" className="pb-2 text-blue-500 text-base sm:text-xl">
                    Voters Information
                  </Typography>
                  <Divider className="my-2" />
                  <Typography variant="body2" className="pt-2 text-sm sm:text-base mb-2">
                    <b>Voter Id</b>: {data.ID_Number}
                  </Typography>
                  <Typography variant="body2" className="text-sm sm:text-base mb-2">
                    <b>County</b>: {toTitleCase(data.County)}
                  </Typography>
                  <div className="mb-2">
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', fontSize: '0.875rem', minWidth: 'fit-content', width: '28px', paddingTop: '6px', flexShrink: 0 }}>
                        Tel:
                      </Typography>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          {data.Home_Phone && data.Home_Phone !== 0 && (
                            <div 
                              style={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px', width: '130px' }}
                              onMouseEnter={(e) => {
                                const deleteBtn = e.currentTarget.querySelector('.delete-icon-phone-existing');
                                if (deleteBtn) deleteBtn.style.display = 'flex';
                              }}
                              onMouseLeave={(e) => {
                                const deleteBtn = e.currentTarget.querySelector('.delete-icon-phone-existing');
                                if (deleteBtn) deleteBtn.style.display = 'none';
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                {Math.floor(data.Home_Phone)}
                              </Typography>
                              <IconButton 
                                className="delete-icon-phone-existing"
                                size="small" 
                                onClick={handleDeleteExistingPhone}
                                sx={{ p: 0.25, ml: 0.5, display: 'none' }}
                              >
                                <DeleteIcon sx={{ fontSize: '14px' }} />
                              </IconButton>
                            </div>
                          )}
                          {additionalPhones.slice(0, data.Home_Phone && data.Home_Phone !== 0 ? 1 : 2).map((phone, index) => (
                            <div 
                              key={index} 
                              style={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px', width: '130px' }}
                              onMouseEnter={(e) => {
                                const deleteBtn = e.currentTarget.querySelector('.delete-icon-phone');
                                if (deleteBtn) deleteBtn.style.display = 'flex';
                              }}
                              onMouseLeave={(e) => {
                                const deleteBtn = e.currentTarget.querySelector('.delete-icon-phone');
                                if (deleteBtn) deleteBtn.style.display = 'none';
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                {phone.phone}
                              </Typography>
                              <IconButton 
                                className="delete-icon-phone"
                                size="small" 
                                onClick={() => handleDeletePhone(phone.phone)}
                                sx={{ p: 0.25, ml: 0.5, display: 'none' }}
                              >
                                <DeleteIcon sx={{ fontSize: '14px' }} />
                              </IconButton>
                            </div>
                          ))}
                        </div>
                        {additionalPhones.length > (data.Home_Phone && data.Home_Phone !== 0 ? 1 : 2) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {additionalPhones.slice(data.Home_Phone && data.Home_Phone !== 0 ? 1 : 2).map((phone, index) => (
                              <div 
                                key={index} 
                                style={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px', width: '130px' }}
                                onMouseEnter={(e) => {
                                  const deleteBtn = e.currentTarget.querySelector('.delete-icon-phone');
                                  if (deleteBtn) deleteBtn.style.display = 'flex';
                                }}
                                onMouseLeave={(e) => {
                                  const deleteBtn = e.currentTarget.querySelector('.delete-icon-phone');
                                  if (deleteBtn) deleteBtn.style.display = 'none';
                                }}
                              >
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                  {phone.phone}
                                </Typography>
                                <IconButton 
                                  className="delete-icon-phone"
                                  size="small" 
                                  onClick={() => handleDeletePhone(phone.phone)}
                                  sx={{ p: 0.25, ml: 0.5, display: 'none' }}
                                >
                                  <DeleteIcon sx={{ fontSize: '14px' }} />
                                </IconButton>
                              </div>
                            ))}
                          </div>
                        )}
                        {isEditingPhone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #1976d2', borderRadius: '4px', padding: '4px 8px', width: '130px', flex: '1 1 auto' }}>
                              <input
                                type="text"
                                value={newPhone}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (/^\d*$/.test(value) && value.length <= 10) {
                                    setNewPhone(value);
                                  }
                                }}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddPhone()}
                                maxLength={10}
                                style={{ 
                                  border: 'none', 
                                  outline: 'none', 
                                  width: '100%',
                                  fontSize: '0.875rem',
                                  fontFamily: 'inherit'
                                }}
                                autoFocus
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <Typography 
                                variant="body2" 
                                component="span" 
                                sx={{ color: '#1976d2', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap' }}
                                onClick={handleAddPhone}
                              >
                                Save
                              </Typography>
                              <Typography 
                                variant="body2" 
                                component="span" 
                                sx={{ color: '#666', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap' }}
                                onClick={() => {
                                  setIsEditingPhone(false);
                                  setNewPhone('');
                                }}
                              >
                                Cancel
                              </Typography>
                            </div>
                          </div>
                        )}
                      </div>
                      {!isEditingPhone && (
                        <div style={{ display: 'flex', gap: '8px', paddingTop: '6px', flexShrink: 0 }}>
                          <Typography 
                            variant="body2" 
                            component="span" 
                            sx={{ 
                              color: ((data.Home_Phone && data.Home_Phone !== 0 ? 1 : 0) + additionalPhones.length) >= 4 ? '#ccc' : '#1976d2', 
                              cursor: ((data.Home_Phone && data.Home_Phone !== 0 ? 1 : 0) + additionalPhones.length) >= 4 ? 'not-allowed' : 'pointer', 
                              fontSize: '0.875rem', 
                              fontWeight: 500,
                              whiteSpace: 'nowrap'
                            }}
                            onClick={() => {
                              const totalContacts = (data.Home_Phone && data.Home_Phone !== 0 ? 1 : 0) + additionalPhones.length;
                              if (totalContacts >= 4) return;
                              setIsEditingPhone(true);
                            }}
                          >
                            + Add
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mb-2">
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', fontSize: '0.875rem', minWidth: 'fit-content', width: '42px', paddingTop: '6px', flexShrink: 0 }}>
                        Email:
                      </Typography>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                          {(data.Email || data.Mail_Country) && (
                            <div 
                              style={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px', minWidth: 'fit-content' }}
                              onMouseEnter={(e) => {
                                const deleteBtn = e.currentTarget.querySelector('.delete-icon-email-existing');
                                if (deleteBtn) deleteBtn.style.display = 'flex';
                              }}
                              onMouseLeave={(e) => {
                                const deleteBtn = e.currentTarget.querySelector('.delete-icon-email-existing');
                                if (deleteBtn) deleteBtn.style.display = 'none';
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                {data.Email || data.Mail_Country}
                              </Typography>
                              <IconButton 
                                className="delete-icon-email-existing"
                                size="small" 
                                onClick={handleDeleteExistingEmail}
                                sx={{ p: 0.25, ml: 0.5, flexShrink: 0, display: 'none' }}
                              >
                                <DeleteIcon sx={{ fontSize: '14px' }} />
                              </IconButton>
                            </div>
                          )}
                          {additionalEmails.slice(0, (data.Email || data.Mail_Country) ? 1 : 2).map((email, index) => (
                            <div 
                              key={index} 
                              style={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px', minWidth: 'fit-content' }}
                              onMouseEnter={(e) => {
                                const deleteBtn = e.currentTarget.querySelector('.delete-icon-email');
                                if (deleteBtn) deleteBtn.style.display = 'flex';
                              }}
                              onMouseLeave={(e) => {
                                const deleteBtn = e.currentTarget.querySelector('.delete-icon-email');
                                if (deleteBtn) deleteBtn.style.display = 'none';
                              }}
                            >
                              <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                {email.email}
                              </Typography>
                              <IconButton 
                                className="delete-icon-email"
                                size="small" 
                                onClick={() => handleDeleteEmail(email.email)}
                                sx={{ p: 0.25, ml: 0.5, flexShrink: 0, display: 'none' }}
                              >
                                <DeleteIcon sx={{ fontSize: '14px' }} />
                              </IconButton>
                            </div>
                          ))}
                        </div>
                        {additionalEmails.length > ((data.Email || data.Mail_Country) ? 1 : 2) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            {additionalEmails.slice((data.Email || data.Mail_Country) ? 1 : 2).map((email, index) => (
                              <div 
                                key={index} 
                                style={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px', minWidth: 'fit-content' }}
                                onMouseEnter={(e) => {
                                  const deleteBtn = e.currentTarget.querySelector('.delete-icon-email');
                                  if (deleteBtn) deleteBtn.style.display = 'flex';
                                }}
                                onMouseLeave={(e) => {
                                  const deleteBtn = e.currentTarget.querySelector('.delete-icon-email');
                                  if (deleteBtn) deleteBtn.style.display = 'none';
                                }}
                              >
                                <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                                  {email.email}
                                </Typography>
                                <IconButton 
                                  className="delete-icon-email"
                                  size="small" 
                                  onClick={() => handleDeleteEmail(email.email)}
                                  sx={{ p: 0.25, ml: 0.5, flexShrink: 0, display: 'none' }}
                                >
                                  <DeleteIcon sx={{ fontSize: '14px' }} />
                                </IconButton>
                              </div>
                            ))}
                          </div>
                        )}
                        {isEditingEmail && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #1976d2', borderRadius: '4px', padding: '4px 8px', minWidth: 'fit-content', flex: '1 1 auto' }}>
                              <input
                                type="text"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                                style={{ 
                                  border: 'none', 
                                  outline: 'none', 
                                  width: '100%',
                                  minWidth: '120px',
                                  fontSize: '0.875rem',
                                  fontFamily: 'inherit'
                                }}
                                autoFocus
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <Typography 
                                variant="body2" 
                                component="span" 
                                sx={{ color: '#1976d2', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap' }}
                                onClick={handleAddEmail}
                              >
                                Save
                              </Typography>
                              <Typography 
                                variant="body2" 
                                component="span" 
                                sx={{ color: '#666', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, whiteSpace: 'nowrap' }}
                                onClick={() => {
                                  setIsEditingEmail(false);
                                  setNewEmail('');
                                }}
                              >
                                Cancel
                              </Typography>
                            </div>
                          </div>
                        )}
                      </div>
                      {!isEditingEmail && (
                        <div style={{ display: 'flex', gap: '8px', paddingTop: '6px', flexShrink: 0 }}>
                          <Typography 
                            variant="body2" 
                            component="span" 
                            sx={{ 
                              color: ((data.Email || data.Mail_Country ? 1 : 0) + additionalEmails.length) >= 4 ? '#ccc' : '#1976d2', 
                              cursor: ((data.Email || data.Mail_Country ? 1 : 0) + additionalEmails.length) >= 4 ? 'not-allowed' : 'pointer', 
                              fontSize: '0.875rem', 
                              fontWeight: 500,
                              whiteSpace: 'nowrap'
                            }}
                            onClick={() => {
                              const totalContacts = (data.Email || data.Mail_Country ? 1 : 0) + additionalEmails.length;
                              if (totalContacts >= 4) return;
                              setIsEditingEmail(true);
                            }}
                          >
                            + Add
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>

                  <Typography variant="body2" className="text-sm sm:text-base mb-2">
                    <b>Address</b>: {Math.floor(data.House_Number)}
                    {data.House_Number_Suffix}
                    <span
                      className="text-blue-500 underline cursor-pointer px-2"
                      onClick={handleStreetLink}
                    >
                      {toTitleCase(data.Street_Name)}
                    </span>
                    <br /> {toTitleCase(data.City)}, {data.State} {data.Zip}
                  </Typography>
                  <Typography variant="body2" className="text-sm sm:text-base mb-2">
                    <b>Precinct: </b>
                    <span
                      className="text-blue-500 underline cursor-pointer px-2"
                      onClick={handlePrecinctNameLink}
                    >
                      {toTitleCase(data.Precinct_Name)}
                    </span>
                  </Typography>
                  <Typography variant="body2" className="text-sm sm:text-base mb-2">
                    <b>State House: </b>
                    <span
                      className="text-blue-500 underline cursor-pointer px-2"
                      onClick={handleStateHouseLink}
                    >
                      {toTitleCase(data.State_House)}
                    </span>
                  </Typography>
                  <Typography variant="body2" className="text-sm sm:text-base mb-2">
                    <b>State Senate: </b>
                    <span
                      className="text-blue-500 underline cursor-pointer px-2"
                      onClick={handleStateSenateLink}
                    >
                      {toTitleCase(data.State_Senate)}
                    </span>
                  </Typography>
                  <Typography variant="body2" className="text-sm sm:text-base mb-2">
                    <b>Congressional: </b>
                    <span
                      className="text-blue-500 underline cursor-pointer px-2"
                      onClick={handleCongressLink}
                    >
                      {toTitleCase(data[`U.S._Congress`])}
                    </span>
                  </Typography>
                  <Typography variant="body2" className="text-sm sm:text-base">
                    <b>School District: </b>
                    <span
                      className="text-blue-500 underline cursor-pointer px-2"
                      onClick={handleStateDistrictLink}
                    >
                      {toTitleCase(data.School_District)}
                    </span>
                  </Typography>
                </CardContent>
              </Card>
            </div>

            <div
              className="flex-1 w-full lg:w-auto"
              style={{ display: 'flex', flexDirection: 'column', height: leftColumnHeight || 'auto' }}
            >
              <Card elevation={3} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent
                  className="px-2 py-3 sm:px-4 sm:py-4"
                  style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                >
                  <Typography variant="h6" className="py-3 text-blue-500 text-base sm:text-xl">
                    Voters
                  </Typography>
                  <Divider className="my-3" />

                  <TableContainer style={{ flex: 1, overflow: 'auto' }} className="overflow-x-auto">
                    <ElectionDataTable data={data} />
                  </TableContainer>
                </CardContent>
              </Card>
            </div>
          </div>
          {showDemocratDataTable ? (
            <div className="bg-gray-100 px-1 sm:px-8">
              <Card elevation={3}>
                <CardContent className="px-2 py-3 sm:px-4 sm:py-4">
                  <Typography variant="h6" className="py-3 text-blue-500 text-base sm:text-xl">
                    Voters
                  </Typography>
                  <Divider className="my-3" />

                  <TableContainer
                    style={{ maxHeight: "26rem" }}
                    className="overflow-y-auto overflow-x-auto"
                  >
                    <DataTable data={democratVoters} />
                  </TableContainer>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </div>
      ) : (
        <>Loading</>
      )}
      {loadingTableData && (
        <div className="px-4 sm:px-16 text-base sm:text-xl">Loading Voter data...</div>
      )}
    </div>
  );
};

export default Profile;
